import React from 'react';
import { Text } from '@shared/components/Text';
import { Colors } from '@shared/styles';
import { FormattedTokenText } from '../FormattedText/FormattedTokenText';
import { encodeTokenPlaceholders } from '../FormattedText/encodeTokenPlaceholders';
import { FormattedTextProps, FormattedTextToken } from '../FormattedText/props';
import { HoverableCopyBlock } from './HoverableCopyBlock';
import { ChatMarkdownBlock, parseDocumentBlocks } from './parseDocumentBlocks';
import { parseChatListLine } from './parseListLine';
import styles from './Markdown.module.css';

type MarkdownCtx = {
  tokenByCode: Map<number, FormattedTextToken>;
  highlightColor: string;
  color?: string;
};

const UNDERSCORE_OPEN_BEFORE = /[\s([{]/;
const UNDERSCORE_CLOSE_AFTER = /[\s)\]}.,:;!?]/;

function canOpenUnderscoreEmphasis(s: string, i: number): boolean {
  if (i === 0) {
    return true;
  }
  return UNDERSCORE_OPEN_BEFORE.test(s[i - 1]);
}

function findClosingSingleStar(s: string, start: number): number {
  for (let j = start; j < s.length; j++) {
    if (s[j] !== '*') {
      continue;
    }
    if (s[j + 1] === '*') {
      continue;
    }
    if (j > 0 && s[j - 1] === '*') {
      continue;
    }
    return j;
  }
  return -1;
}

function findClosingSingleUnderscore(s: string, start: number): number {
  for (let j = start; j < s.length; j++) {
    if (s[j] !== '_') {
      continue;
    }
    if (s[j + 1] === '_') {
      continue;
    }
    if (j > 0 && s[j - 1] === '_') {
      continue;
    }
    if (/\s/.test(s[j - 1])) {
      continue;
    }
    const after = s[j + 1];
    if (after !== undefined && !UNDERSCORE_CLOSE_AFTER.test(after)) {
      continue;
    }
    return j;
  }
  return -1;
}

function findClosingDoubleUnderscore(s: string, start: number): number {
  let idx = start;
  while (idx < s.length - 1) {
    const pos = s.indexOf('__', idx);
    if (pos === -1) {
      return -1;
    }
    if (/\s/.test(s[pos - 1])) {
      idx = pos + 1;
      continue;
    }
    const after = s[pos + 2];
    if (after !== undefined && !UNDERSCORE_CLOSE_AFTER.test(after)) {
      idx = pos + 1;
      continue;
    }
    return pos;
  }
  return -1;
}

function findClosingSingleTilde(s: string, start: number): number {
  for (let j = start; j < s.length; j++) {
    if (s[j] !== '~') {
      continue;
    }
    if (s[j + 1] === '~') {
      continue;
    }
    if (j > 0 && s[j - 1] === '~') {
      continue;
    }
    if (/\s/.test(s[j - 1])) {
      continue;
    }
    const after = s[j + 1];
    if (after !== undefined && !UNDERSCORE_CLOSE_AFTER.test(after)) {
      continue;
    }
    return j;
  }
  return -1;
}

function findClosingDoubleTilde(s: string, start: number): number {
  let idx = start;
  while (idx < s.length - 1) {
    const pos = s.indexOf('~~', idx);
    if (pos === -1) {
      return -1;
    }
    if (/\s/.test(s[pos - 1])) {
      idx = pos + 1;
      continue;
    }
    const after = s[pos + 2];
    if (after !== undefined && !UNDERSCORE_CLOSE_AFTER.test(after)) {
      idx = pos + 1;
      continue;
    }
    return pos;
  }
  return -1;
}

function parseInlineRich(s: string, ctx: MarkdownCtx, keyPrefix: string): React.ReactNode[] {
  if (!s) {
    return [];
  }

  const out: React.ReactNode[] = [];
  let i = 0;
  let buf = '';
  let part = 0;

  const flushBuf = () => {
    if (buf) {
      out.push(buf);
      buf = '';
    }
  };

  while (i < s.length) {
    const code = s.charCodeAt(i);
    if (ctx.tokenByCode.has(code)) {
      flushBuf();
      const token = ctx.tokenByCode.get(code)!;
      out.push(
        <FormattedTokenText
          key={`${keyPrefix}-ph-${part++}`}
          color={ctx.color}
          highlightColor={ctx.highlightColor}
          token={token}
        >
          {token.value}
        </FormattedTokenText>,
      );
      i++;
      continue;
    }

    if (s[i] === '`') {
      flushBuf();
      let j = i + 1;
      while (j < s.length && s[j] !== '`' && s[j] !== '\n') {
        j++;
      }
      if (j < s.length && s[j] === '`') {
        const inner = decodePlaceholdersToPlainText(s.slice(i + 1, j), ctx);
        out.push(
          <span key={`${keyPrefix}-ic-${part++}`} className={styles.inlineCode}>
            <Text size="body2" color={ctx.color} monospace>
              {inner}
            </Text>
          </span>,
        );
        i = j + 1;
        continue;
      }
      buf += s[i];
      i++;
      continue;
    }

    if (s.slice(i, i + 2) === '**') {
      flushBuf();
      const end = s.indexOf('**', i + 2);
      if (end !== -1) {
        const inner = s.slice(i + 2, end);
        out.push(
          <Text key={`${keyPrefix}-b-${part++}`} weight="bold" color={ctx.color}>
            {parseInlineRich(inner, ctx, `${keyPrefix}B${part}`)}
          </Text>,
        );
        i = end + 2;
        continue;
      }
      buf += s[i];
      i++;
      continue;
    }

    if (s.slice(i, i + 2) === '__' && canOpenUnderscoreEmphasis(s, i)) {
      flushBuf();
      const end = findClosingDoubleUnderscore(s, i + 2);
      if (end !== -1) {
        const inner = s.slice(i + 2, end);
        out.push(
          <Text key={`${keyPrefix}-b2-${part++}`} weight="bold" color={ctx.color}>
            {parseInlineRich(inner, ctx, `${keyPrefix}U${part}`)}
          </Text>,
        );
        i = end + 2;
        continue;
      }
      buf += s[i];
      i++;
      continue;
    }

    if (s.slice(i, i + 2) === '~~') {
      flushBuf();
      const end = findClosingDoubleTilde(s, i + 2);
      if (end !== -1) {
        const inner = s.slice(i + 2, end);
        out.push(
          <span key={`${keyPrefix}-s2-${part++}`} className={styles.strikethrough}>
            {parseInlineRich(inner, ctx, `${keyPrefix}S${part}`)}
          </span>,
        );
        i = end + 2;
        continue;
      }
      buf += s[i];
      i++;
      continue;
    }

    if (s[i] === '~' && s[i + 1] !== '~') {
      flushBuf();
      const close = findClosingSingleTilde(s, i + 1);
      if (close !== -1) {
        const inner = s.slice(i + 1, close);
        out.push(
          <span key={`${keyPrefix}-s-${part++}`} className={styles.strikethrough}>
            {parseInlineRich(inner, ctx, `${keyPrefix}T${part}`)}
          </span>,
        );
        i = close + 1;
        continue;
      }
    }

    if (s[i] === '*' && s[i + 1] !== '*') {
      flushBuf();
      const close = findClosingSingleStar(s, i + 1);
      if (close !== -1) {
        const inner = s.slice(i + 1, close);
        out.push(
          <Text key={`${keyPrefix}-i-${part++}`} italic color={ctx.color}>
            {parseInlineRich(inner, ctx, `${keyPrefix}I${part}`)}
          </Text>,
        );
        i = close + 1;
        continue;
      }
    }

    if (s[i] === '_' && s[i + 1] !== '_' && canOpenUnderscoreEmphasis(s, i)) {
      flushBuf();
      const close = findClosingSingleUnderscore(s, i + 1);
      if (close !== -1) {
        const inner = s.slice(i + 1, close);
        out.push(
          <Text key={`${keyPrefix}-iu-${part++}`} italic color={ctx.color}>
            {parseInlineRich(inner, ctx, `${keyPrefix}J${part}`)}
          </Text>,
        );
        i = close + 1;
        continue;
      }
    }

    buf += s[i];
    i++;
  }

  flushBuf();
  return out;
}

function decodePlaceholdersToPlainText(s: string, ctx: MarkdownCtx): string {
  let out = '';
  for (let i = 0; i < s.length; i++) {
    const code = s.charCodeAt(i);
    if (ctx.tokenByCode.has(code)) {
      out += ctx.tokenByCode.get(code)!.value;
    } else {
      out += s[i];
    }
  }
  return out;
}

function ParagraphBlock({ body, ctx }: { body: string; ctx: MarkdownCtx }) {
  const lines = body.split('\n');
  const nodes: React.ReactNode[] = [];

  lines.forEach((line, idx) => {
    if (idx > 0) {
      nodes.push('\n');
    }
    const listLine = parseChatListLine(line);
    if (listLine) {
      const indent = listLine.indent;
      const content = listLine.content;
      let bullet = '';
      if (listLine.kind === 'bullet') {
        bullet = '\u2022 ';
      } else {
        bullet = `${listLine.number}. `;
      }
      nodes.push(indent, bullet, ...parseInlineRich(content, ctx, `ln-${idx}`));
    } else {
      nodes.push(...parseInlineRich(line, ctx, `ln-${idx}`));
    }
  });

  return (
    <Text block color={ctx.color} className={styles.paragraph}>
      {nodes}
    </Text>
  );
}

function FenceBlock({ body, ctx }: { body: string; ctx: MarkdownCtx }) {
  const plainBody = decodePlaceholdersToPlainText(body, ctx);
  return (
    <HoverableCopyBlock text={plainBody}>
      <div className={styles.fenceScroll}>
        <Text size="body2" color={ctx.color} selectable monospace>
          {plainBody}
        </Text>
      </div>
    </HoverableCopyBlock>
  );
}

function QuoteBlock({ body, ctx }: { body: string; ctx: MarkdownCtx }) {
  return (
    <div className={styles.quote}>
      <Text color={ctx.color} className={styles.quoteText}>
        {parseInlineRich(body, ctx, 'quote')}
      </Text>
    </div>
  );
}

function parseTableRow(row: string): string[] {
  const trimmed = row.trim();
  const parts = trimmed.split('|');
  const start = trimmed.startsWith('|') ? 1 : 0;
  const end = trimmed.endsWith('|') ? parts.length - 1 : parts.length;
  return parts.slice(start, end).map(c => c.trim());
}

function TableBlock({ body, ctx }: { body: string; ctx: MarkdownCtx }) {
  const rows = body.split('\n').filter(row => row.trim().length > 0);
  if (rows.length < 2) {
    return <ParagraphBlock body={body} ctx={ctx} />;
  }

  const [headerRow, , ...dataRows] = rows;
  const headers = parseTableRow(headerRow);

  return (
    <HoverableCopyBlock text={body}>
      <div className={styles.tableScroll}>
        <table className={styles.table}>
          <thead>
            <tr>
              {headers.map((cell, i) => (
                <th key={i} className={styles.th}>
                  <Text weight="bold" color={ctx.color}>
                    {parseInlineRich(cell, ctx, `th-${i}`)}
                  </Text>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {dataRows.map((row, ri) => (
              <tr key={ri}>
                {parseTableRow(row).map((cell, ci) => (
                  <td key={ci} className={styles.td}>
                    <Text color={ctx.color}>{parseInlineRich(cell, ctx, `td-${ri}-${ci}`)}</Text>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </HoverableCopyBlock>
  );
}

function HeaderBlock({
  level,
  body,
  ctx,
}: {
  level: 1 | 2 | 3 | 4;
  body: string;
  ctx: MarkdownCtx;
}) {
  return (
    <Text data-header={level} color={ctx.color} className={styles.header}>
      {parseInlineRich(body, ctx, `h${level}`)}
    </Text>
  );
}

function Block({ block, ctx }: { block: ChatMarkdownBlock; ctx: MarkdownCtx }) {
  if (block.kind === 'paragraph') {
    return <ParagraphBlock body={block.body} ctx={ctx} />;
  }
  if (block.kind === 'fence') {
    return <FenceBlock body={block.body} ctx={ctx} />;
  }
  if (block.kind === 'table') {
    return <TableBlock body={block.body} ctx={ctx} />;
  }
  if (block.kind === 'header') {
    return <HeaderBlock level={block.level} body={block.body} ctx={ctx} />;
  }
  return <QuoteBlock body={block.body} ctx={ctx} />;
}

export function Markdown({
  children: text,
  tokens,
  highlightColor = Colors.v2.purple,
  markdown: _markdown,
  color,
}: FormattedTextProps) {
  const { encoded, tokenByCharCode } = encodeTokenPlaceholders(text, tokens);
  const blocks = parseDocumentBlocks(encoded);

  const ctx: MarkdownCtx = {
    tokenByCode: tokenByCharCode,
    highlightColor,
    color,
  };

  if (blocks.length === 0) {
    return (
      <Text color={color} className={styles.paragraph}>
        {text}
      </Text>
    );
  }

  if (blocks.length === 1) {
    return <Block block={blocks[0]} ctx={ctx} />;
  }

  return (
    <div className={styles.root}>
      {blocks.map((block, index) => (
        <div key={`blk-${index}`} className={styles.block}>
          <Block block={block} ctx={ctx} />
        </div>
      ))}
    </div>
  );
}
